import { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode, Ref } from 'react';

import { Styles, css, cx } from '@flipster/styled-system/css';
import { ChoiceChipVariant, choiceChip } from '@flipster/styled-system/recipes';

import { useMeasure } from '../../../misc/browser/measure';
import { useMergeRef } from '../../../misc/ref/use-merge-ref';

export type ChoiceChipVariantType = ChoiceChipVariant['type'];

interface ChoiceChipCommonProps {
  type?: ChoiceChipVariantType;
  icon?: ReactNode;
  label?: ReactNode;
  badge?: ReactNode;
  selected?: boolean;
  redDot?: boolean;
  css?: Styles;
}

export type ChoiceChipAsButtonProps = ChoiceChipCommonProps & {
  as?: 'button';
  ref?: Ref<HTMLButtonElement>;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type' | 'className' | 'style'>;

export type ChoiceChipAsAnchorProps = ChoiceChipCommonProps & {
  as: 'a';
  ref?: Ref<HTMLAnchorElement>;
} & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'type' | 'className' | 'style'>;

export type ChoiceChipProps = ChoiceChipAsButtonProps | ChoiceChipAsAnchorProps;

const useChoiceChipRender = ({
  selected,
  type = 'rounded',
  redDot,
  css: cssProps,
  ref: forwardedRef,
}: {
  selected?: boolean;
  type?: ChoiceChipVariantType;
  redDot?: boolean;
  css?: Styles;
  ref?: Ref<HTMLElement>;
}) => {
  const classes = choiceChip({ selected, type, redDot });
  const [measureRef, { width, height }] = useMeasure<HTMLElement>();
  const ref = useMergeRef(measureRef, forwardedRef);
  const showRedDot = redDot && !selected;
  const containerClassName = cx(classes.container, css(cssProps));
  const containerStyle = showRedDot
    ? {
        clipPath: `path("M -2 -2 V ${height + 2} H ${width + 2} V 4.5 H ${width} a 4.5 4.5 0 1 1 -9 0 a 4.5 4.5 0 1 1 9 0 h 2 V -2 Z")`,
      }
    : undefined;

  return { classes, ref, showRedDot, containerClassName, containerStyle, selected };
};

const ChipContent = ({
  classes,
  icon,
  label,
  badge,
}: Pick<ChoiceChipCommonProps, 'icon' | 'label' | 'badge'> & { classes: ReturnType<typeof choiceChip> }) => (
  <>
    {icon && <div className={classes.icon}>{icon}</div>}
    {label != null && <div className={classes.label}>{label}</div>}
    {badge != null && <div className={classes.badge}>{badge}</div>}
  </>
);

export const ChoiceChip = (props: ChoiceChipProps) => {
  const { as, ref: forwardedRef, type, icon, label, badge, selected, redDot, css: cssProps, ...restProps } = props;
  const { classes, ref, showRedDot, containerClassName, containerStyle } = useChoiceChipRender({
    selected,
    type,
    redDot,
    css: cssProps,
    ref: forwardedRef as Ref<HTMLElement>,
  });

  return (
    <div className={classes.root}>
      {as === 'a' ? (
        <a
          data-active={selected || undefined}
          ref={ref as Ref<HTMLAnchorElement>}
          className={containerClassName}
          style={containerStyle}
          {...(restProps as Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'type' | 'className' | 'style'>)}
        >
          <ChipContent classes={classes} icon={icon} label={label} badge={badge} />
        </a>
      ) : (
        <button
          type='button'
          data-active={selected || undefined}
          ref={ref as Ref<HTMLButtonElement>}
          className={containerClassName}
          style={containerStyle}
          {...(restProps as Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type' | 'className' | 'style'>)}
        >
          <ChipContent classes={classes} icon={icon} label={label} badge={badge} />
        </button>
      )}
      {showRedDot && <div className={classes.redDot} />}
    </div>
  );
};
